import React, { useEffect, useState } from "react";
import { X, KeyRound, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "@/context/AdminAuthContext";

/**
 * Authenticated password-change modal. Calls POST /api/auth/change-password
 * via apiFetch (which attaches the Bearer token). On success, signs the
 * admin out so they have to re-login with the new password — this also
 * invalidates any other tab still using the old session in their head.
 */
export default function ChangePasswordModal({ open, onClose }) {
  const { apiFetch, logout } = useAdminAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setCurrent(""); setNext(""); setConfirm("");
    setShowCurrent(false); setShowNext(false);
    setError("");
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) { setError("New password must be at least 8 characters"); return; }
    if (next !== confirm) { setError("New password and confirmation do not match"); return; }
    if (next === current) { setError("New password must differ from current password"); return; }
    setBusy(true);
    try {
      const res = await apiFetch("/auth/change-password", {
        method: "POST",
        body: JSON.stringify({ current_password: current, new_password: next }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Failed to change password");
      toast.success("Password changed — please sign in again");
      onClose && onClose();
      // Force a fresh login with the new password.
      setTimeout(() => { logout(); }, 600);
    } catch (err) {
      setError(err?.message || "Failed to change password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="change-password-modal">
      <form onSubmit={submit} className="w-full max-w-md bg-[#141414] rounded-2xl border border-white/10 shadow-2xl">
        <header className="flex items-center justify-between p-5 border-b border-white/5">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#F37021]/15 flex items-center justify-center">
              <KeyRound className="w-4 h-4 text-[#F37021]" />
            </div>
            <h3 className="font-display text-lg font-bold">Change password</h3>
          </div>
          <button type="button" onClick={onClose} className="text-white/40 hover:text-white" data-testid="change-password-close">
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="p-5 space-y-4">
          <PasswordField
            label="Current password"
            value={current}
            onChange={setCurrent}
            show={showCurrent}
            onToggle={() => setShowCurrent((v) => !v)}
            testid="cp-current"
            autoFocus
          />
          <PasswordField
            label="New password"
            hint="Minimum 8 characters"
            value={next}
            onChange={setNext}
            show={showNext}
            onToggle={() => setShowNext((v) => !v)}
            testid="cp-new"
          />
          <PasswordField
            label="Confirm new password"
            value={confirm}
            onChange={setConfirm}
            show={showNext}
            onToggle={() => setShowNext((v) => !v)}
            testid="cp-confirm"
          />

          {error && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-sm text-red-300" data-testid="cp-error">
              {error}
            </div>
          )}

          <p className="text-[11px] text-white/40 leading-relaxed">
            Once you change your password, the value in <code className="text-white/60">backend/.env</code> is no longer
            used to seed the admin on restart. You can still revert by clearing
            the <code className="text-white/60">password_managed_by_env</code> flag in Mongo and restarting.
          </p>
        </div>

        <footer className="flex items-center justify-end gap-2 p-5 border-t border-white/5">
          <button type="button" onClick={onClose} className="h-10 px-5 rounded-full bg-white/5 hover:bg-white/10 text-sm font-semibold" data-testid="cp-cancel">
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="h-10 px-6 rounded-full bg-[#F37021] hover:bg-[#E06015] text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="cp-submit"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Change password
          </button>
        </footer>
      </form>
    </div>
  );
}

function PasswordField({ label, hint, value, onChange, show, onToggle, testid, autoFocus }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-white/60">{label}</span>
      {hint && <span className="ms-2 text-[10px] text-white/30">{hint}</span>}
      <div className="mt-1.5 relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
          autoFocus={autoFocus}
          className="w-full h-10 ps-3 pe-10 rounded-xl bg-black/40 border border-white/10 text-sm focus:outline-none focus:border-[#F37021]"
          data-testid={testid}
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute end-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white p-1"
          tabIndex={-1}
          aria-label={show ? "Hide password" : "Show password"}
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </label>
  );
}
